// Libs
import { Avatar, Box, Button, ListItemText, Menu, MenuItem, Stack, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useContext, useState } from 'react';
// Others
import { EMPTY_STRING, ROLES } from '~/utils/constants/common';
import { StorageEnum } from '~/utils/enum/base';
import { authRouteAbsolute } from '~/utils/routes/auth';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { getAvatarName, getFullName, getInitials } from '~/utils/helper/common';
import { WebSocketContext } from '~/context';
import { authActions } from '~/thunks/auth/authSlice';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { RoleEnum } from '~/utils/enum/common';
import { staffRouteAbsolute } from '~/utils/routes/staff';
// Styles, images, icons
import styles from './DropdownProfile.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const DropdownProfile = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base', 'common/sidebar']);
  const navigate = useNavigate();
  const { wss } = useContext(WebSocketContext);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  const userRole = localStorage.getItem(StorageEnum.ROLE);
  const { profile } = useAppSelector((state) => state.user);
  //#endregion Selector

  //#region Declare State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const roleName = () => {
    const role = ROLES.find((role) => role.id === userRole);

    return role?.name || EMPTY_STRING;
  };

  const handleShowMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleEditProfile = () => {
    handleCloseMenu();
    switch (userRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.profile);
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.profile);
        break;

      default:
        break;
    }
  };

  const handleAccountSetting = () => {
    handleCloseMenu();
    navigate(adminRouteAbsolute.settings);
  };

  const handleLogout = () => {
    handleCloseMenu();
    localStorage.removeItem(StorageEnum.ACCESS_TOKEN);
    localStorage.removeItem(StorageEnum.REFRESH_TOKEN);
    localStorage.removeItem(StorageEnum.STATUS_LOGIN);
    localStorage.removeItem(StorageEnum.DEVICE_ID);
    localStorage.removeItem(StorageEnum.ROLE);
    navigate(`${authRouteAbsolute.login}`);

    wss?.close();

    dispatch(authActions.setAccessToken(null));
  };

  //#endregion Handle Function

  return (
    <Stack component={'div'} id='dropdownProfileComponent'>
      <Button
        variant='outlined'
        color='inherit'
        className={cx('headerUserInfo')}
        onClick={(event) => {
          event.stopPropagation();
          handleShowMenu(event);
        }}
      >
        {profile?.avatarImage ? (
          <Avatar src={profile?.avatarImage} alt={t('common_alt_img')} />
        ) : (
          <Avatar src={profile?.avatarImage} alt={t('common_alt_img')}>
            <Typography className={cx('avatarUserName')}>
              {getInitials({ firstName: profile?.firstName, lastName: profile?.lastName })}
            </Typography>
          </Avatar>
        )}

        <Stack spacing={'3px'} justifyContent={'center'} alignItems={'flex-start'}>
          <Typography className={cx('headerUserName')}>{getFullName({ ...profile })}</Typography>

          <Typography className={cx('headerUserRole')}>{roleName()}</Typography>
        </Stack>
      </Button>

      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={(event: React.MouseEvent) => {
          event.stopPropagation();
          handleCloseMenu();
        }}
        slotProps={{
          paper: {
            className: cx('paper'),
          },
        }}
        MenuListProps={{ className: cx('menuList') }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        autoFocus={false}
      >
        <Box className={cx('viewHeaderActionModal')}>
          <Avatar
            src={profile?.avatarImage}
            alt={t('common_alt_img', { ns: 'common/base' })}
            className={cx('avatarUserName')}
          >
            {getFullName({ ...profile }) && getAvatarName(getFullName({ ...profile }))}
          </Avatar>

          <Stack spacing={'3px'} justifyContent={'center'} alignItems={'flex-start'}>
            <Typography className={cx('headerActionName')}>{getFullName({ ...profile })}</Typography>

            <Typography className={cx('headerActionEmail')}>{profile?.email}</Typography>
          </Stack>
        </Box>

        <MenuItem
          className={cx('itemButton')}
          onClick={(event) => {
            event.stopPropagation();
            handleEditProfile();
          }}
        >
          <Box className={cx('itemIcon')}>
            <img src={icons.profileIconProfile} alt={t('common_alt_img')} className={cx('itemIconImg')} />
          </Box>

          <ListItemText
            primary={
              <Typography className={cx('itemText')}>
                {t('menu_profile_edit', { ns: 'common/sidebar' })}
              </Typography>
            }
          />
        </MenuItem>

        {userRole === RoleEnum.ADMIN && (
          <MenuItem
            className={cx('itemButton')}
            onClick={(event) => {
              event.stopPropagation();
              handleAccountSetting();
            }}
          >
            <Box className={cx('itemIcon')}>
              <img src={icons.profileIconSetting} alt={t('common_alt_img')} className={cx('itemIconImg')} />
            </Box>

            <ListItemText
              primary={
                <Typography className={cx('itemText')}>
                  {t('menu_profile_account_setting', { ns: 'common/sidebar' })}
                </Typography>
              }
            />
          </MenuItem>
        )}

        <MenuItem
          className={cx('itemButton', 'logout')}
          onClick={(event) => {
            event.stopPropagation();
            handleLogout();
          }}
        >
          <Box className={cx('itemIcon')}>
            <img src={icons.profileIconLogout} alt={t('common_alt_img')} className={cx('itemIconImg')} />
          </Box>

          <ListItemText
            primary={
              <Typography className={cx('itemText')}>
                {t('menu_profile_log_out', { ns: 'common/sidebar' })}
              </Typography>
            }
          />
        </MenuItem>
      </Menu>
    </Stack>
  );
};

export default DropdownProfile;
